import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { apiConfig } from "../../configs/api";
import type { CommonMetricContext } from "../../entities/forecast_cell_context";
import type { CommonViewContextV1 } from "../../entities/common_view_context";
import { Run } from "../../services/appsync/types";
import { useCustomer } from "../app/provider";
import {
  GetFreshIdToken,
  GetFreshIdTokenOptions,
  IdTokenRefreshError,
  IdTokenContext,
  isTerminalAuthSessionError,
} from "../authentication/id_token_context";
import { VersionedDataContext } from "../versioned_data/context";
import { ChatContext } from "./context";
import {
  CommonCurrentViewDataV1,
  CommonScopeIntent,
  EmbeddedTurnRequest,
  isAllowedNucleusStreamUrl,
  isCommonCurrentViewDataReceipt,
  NucleusSseError,
  NucleusReconciliationStatus,
  NucleusTraceStatus,
  NucleusTurnEvent,
  streamNucleusTurn,
} from "./nucleus_sse_client";
import {
  getNucleusConversation,
  getNucleusConversationPage,
  listNucleusConversations,
  nucleusConversationScopeForRun,
  nucleusConversationScopesMatch,
  NucleusConversationClientError,
  parseCustomerEvidenceReference,
  type CustomerEvidenceReference,
  type NucleusConversationDetail,
  type NucleusConversationDetailPage,
  type NucleusConversationScope,
  type NucleusConversationSummary,
} from "./nucleus_conversation_client";
import {
  AgentState,
  ChatContextType,
  ChatHistoryItem,
  Message,
  PlatformConversationHistoryItem,
  QueuedChatPrompt,
} from "./type";

const initialAgentState: AgentState = {
  status: "idle",
  currentIteration: 0,
  maxIterations: 0,
  reasoning: "",
  streamedText: "",
  reasoningHistory: [],
  sqlQueries: [],
  docSearches: [],
  charts: [],
  toolCalls: [],
  genericTables: [],
  workSteps: [],
  answerStarted: false,
  turnPhases: {
    understand: false,
    gather: false,
    analyze: false,
    answer: false,
  },
};

const safeFailureMessage =
  "Nucleus couldn't complete that response. Please try again.";
const authenticationUnavailableMessage =
  "Authentication is temporarily unavailable. Please try again.";
const connectionTimeoutMessage =
  "Nucleus took too long to connect. Please try again.";
const cancellationMessage = "Response stopped before it was complete.";
const defaultReadinessTimeoutMs = 8_000;
const maxConversationHistoryPages = 20;

type ReadinessProbe = (input: {
  streamUrl: string;
  signal: AbortSignal;
}) => Promise<boolean>;

type ResultSnapshot = {
  status: NucleusTraceStatus;
  reconciliationStatus: NucleusReconciliationStatus;
  evidenceIds: string[];
};

type ConnectionAttempt = {
  context: string;
  controller: AbortController;
  generation: number;
  key: string;
  organizationId: string;
  promise: Promise<boolean>;
  run: Run;
  surfacePath: string;
};

type RegisteredViewContext = Readonly<{
  registration: object;
  organizationId: string | null;
  viewContext: CommonViewContextV1;
}>;

type RegisteredCurrentViewData = Readonly<{
  registration: object;
  organizationId: string | null;
  runScopeIdentity: string | null;
  data: CommonCurrentViewDataV1;
  viewContext: CommonViewContextV1;
}>;

type RegisteredActiveMetricContext = Readonly<{
  registration: object;
  organizationId: string | null;
  metricContext: CommonMetricContext;
  viewContext: CommonViewContextV1;
}>;

type PlatformQueuedTurn = Readonly<{
  id: string;
  dedupeKey: string;
  conversationId: string;
  organizationId: string;
  runScopeIdentity: string;
  scopeKey: string;
  surfacePath: string;
  request: EmbeddedTurnRequest;
}>;

type QueuedTurnPauseReason =
  | "authentication_unavailable"
  | "local_abort"
  | "transport_unknown";

const queuedTurnPauseMessage = (reason: QueuedTurnPauseReason): string =>
  reason === "authentication_unavailable"
    ? "Queue paused because authentication is temporarily unavailable. Retry when you're ready."
    : "Queue paused until Nucleus confirms the previous response ended. Start a new conversation to continue safely.";

const queuedTurnDedupeKey = (request: EmbeddedTurnRequest): string =>
  JSON.stringify({
    version: request.version,
    conversationId: request.conversationId ?? null,
    organizationId: request.organizationId,
    message: request.message.text.trim().replace(/\s+/g, " ").toLowerCase(),
    metricContext: request.metricContext ?? null,
    viewContext: request.viewContext ?? null,
    currentViewData: request.currentViewData ?? null,
    scopeIntent: request.scopeIntent ?? null,
  });

class NucleusSessionExpiredError extends Error {
  constructor() {
    super("The authenticated session is no longer usable.");
    this.name = "NucleusSessionExpiredError";
  }
}

class NucleusAuthenticationUnavailableError extends Error {
  constructor() {
    super(authenticationUnavailableMessage);
    this.name = "NucleusAuthenticationUnavailableError";
  }
}

class NucleusConnectionTimeoutError extends Error {
  constructor() {
    super(connectionTimeoutMessage);
    this.name = "NucleusConnectionTimeoutError";
  }
}

class NucleusRequestAbortedError extends Error {
  constructor() {
    super("The authenticated request was cancelled.");
    this.name = "NucleusRequestAbortedError";
  }
}

const normalizeAuthenticationError = (error: unknown): Error => {
  if (
    error instanceof NucleusSessionExpiredError ||
    error instanceof NucleusAuthenticationUnavailableError
  ) {
    return error;
  }
  return error instanceof IdTokenRefreshError ||
    isTerminalAuthSessionError(error)
    ? new NucleusSessionExpiredError()
    : new NucleusAuthenticationUnavailableError();
};

type RetryablePlatformFailure = Readonly<{
  kind: "authentication_unavailable" | "connection_timeout";
  message: string;
}>;

const retryablePlatformFailure = (
  error: unknown,
): RetryablePlatformFailure | undefined => {
  if (error instanceof NucleusAuthenticationUnavailableError) {
    return {
      kind: "authentication_unavailable",
      message: authenticationUnavailableMessage,
    };
  }
  if (error instanceof NucleusConnectionTimeoutError) {
    return {
      kind: "connection_timeout",
      message: connectionTimeoutMessage,
    };
  }
  return undefined;
};

const authenticationFailureMessage = (error: unknown): string | undefined =>
  error instanceof NucleusAuthenticationUnavailableError
    ? authenticationUnavailableMessage
    : undefined;

type ScopedPlatformConnectionFailure = RetryablePlatformFailure &
  Readonly<{ scopeKey: string }>;

type FreshIdTokenResult = string | undefined | ReturnType<GetFreshIdToken>;
type RequiredFreshIdTokenResult = string | Promise<string>;

const isPromiseLike = <T,>(value: unknown): value is PromiseLike<T> =>
  typeof value === "object" &&
  value !== null &&
  "then" in value &&
  typeof (value as { then?: unknown }).then === "function";

const isConversationUnauthorized = (error: unknown): boolean =>
  error instanceof NucleusConversationClientError && error.status === 401;

const requireTokenWithinRequestBoundary = ({
  requireToken,
  options,
  signal,
  timeoutMs,
}: {
  requireToken: (
    options?: GetFreshIdTokenOptions,
  ) => RequiredFreshIdTokenResult;
  options?: GetFreshIdTokenOptions;
  signal: AbortSignal;
  timeoutMs: number;
}): RequiredFreshIdTokenResult => {
  if (signal.aborted) throw new NucleusRequestAbortedError();
  const token = requireToken(options);
  // Preserve synchronous injected-token behavior; only asynchronous session
  // acquisition needs an abort listener and timeout boundary.
  if (!isPromiseLike<string>(token)) {
    if (signal.aborted) throw new NucleusRequestAbortedError();
    return token;
  }

  return new Promise<string>((resolve, reject) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const cleanup = () => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      signal.removeEventListener("abort", handleAbort);
    };
    const succeed = (freshToken: string) => {
      if (settled) return;
      if (signal.aborted) {
        fail(new NucleusRequestAbortedError());
        return;
      }
      settled = true;
      cleanup();
      resolve(freshToken);
    };
    const fail = (error: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };
    const handleAbort = () => fail(new NucleusRequestAbortedError());

    signal.addEventListener("abort", handleAbort, { once: true });
    if (signal.aborted) {
      handleAbort();
      return;
    }
    timeoutId = setTimeout(
      () => fail(new NucleusAuthenticationUnavailableError()),
      Math.max(1, timeoutMs),
    );
    void Promise.resolve(token).then(succeed, fail);
  });
};

const requestWithOneForcedRefresh = <T,>({
  requireToken,
  request,
  isUnauthorized,
  signal,
  tokenTimeoutMs,
}: {
  requireToken: (
    options?: GetFreshIdTokenOptions,
  ) => RequiredFreshIdTokenResult;
  request: (token: string) => Promise<T>;
  isUnauthorized: (error: unknown) => boolean;
  signal: AbortSignal;
  tokenTimeoutMs: number;
}): Promise<T> => {
  const invoke = (token: string): Promise<T> => {
    if (signal.aborted) {
      return Promise.reject(new NucleusRequestAbortedError());
    }
    try {
      return Promise.resolve(request(token));
    } catch (error) {
      return Promise.reject(error);
    }
  };
  const retry = (error: unknown): Promise<T> => {
    if (signal.aborted || !isUnauthorized(error)) {
      return Promise.reject(error);
    }

    let refreshedToken: RequiredFreshIdTokenResult;
    try {
      refreshedToken = requireTokenWithinRequestBoundary({
        requireToken,
        options: { forceRefresh: true },
        signal,
        timeoutMs: tokenTimeoutMs,
      });
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
    const invokeRetry = (token: string): Promise<T> =>
      invoke(token).catch((retryError: unknown) => {
        if (isUnauthorized(retryError)) {
          // Cognito produced a locally fresh token, so a second backend
          // rejection is transient backend authentication unavailability,
          // not proof that the user's Cognito session expired.
          throw new NucleusAuthenticationUnavailableError();
        }
        throw retryError;
      });
    return isPromiseLike<string>(refreshedToken)
      ? Promise.resolve(refreshedToken).then(invokeRetry)
      : invokeRetry(refreshedToken);
  };

  let token: RequiredFreshIdTokenResult;
  try {
    token = requireTokenWithinRequestBoundary({
      requireToken,
      signal,
      timeoutMs: tokenTimeoutMs,
    });
  } catch (error) {
    return Promise.reject(error);
  }
  return isPromiseLike<string>(token)
    ? Promise.resolve(token).then(invoke).catch(retry)
    : invoke(token).catch(retry);
};

const sameViewContext = (
  left: CommonViewContextV1,
  right: CommonViewContextV1,
): boolean => JSON.stringify(left) === JSON.stringify(right);

const defaultGetCurrentPath = (): string => window.location.pathname;

const defaultCreateId = (): string => {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const normalizeSurfacePath = (rawPath: string): string => {
  const withoutQuery = rawPath.trim().split(/[?#]/, 1)[0] || "/";
  const withLeadingSlash = withoutQuery.startsWith("/")
    ? withoutQuery
    : `/${withoutQuery}`;
  const collapsed = withLeadingSlash.replace(/\/{2,}/g, "/");
  const withoutTrailingSlash =
    collapsed.length > 1 ? collapsed.replace(/\/+$/, "") : collapsed;
  return withoutTrailingSlash.slice(0, 256) || "/";
};

const currentViewSurfacePathMatches = (
  surfaceId: CommonCurrentViewDataV1["surfaceId"],
  surfacePath: string,
): boolean =>
  surfaceId === "inventory-health"
    ? [
        "/inventory-health",
        "/inventory-health-with-transfers",
        "/inventory-health-for-hubs",
        "/raw-inventory-health",
      ].includes(surfacePath)
    : surfacePath === `/${surfaceId}`;

const safeEvidenceLabel = (value: string): string =>
  value.trim().replace(/\s+/g, " ").slice(0, 200);

const runScopeIdentity = (run: Run | null): string | null =>
  run
    ? [
        run.runSettings.organizationId,
        run.runSettings.inputId,
        run.runSettings.workflowId,
        run.runSettings.settingsNameAndVersion,
        run.runSettings.runAttemptId,
      ].join("\u0000")
    : null;

const connectionScopeIdentity = ({
  organizationId,
  run,
}: {
  organizationId: string;
  run: Run;
}): string => [organizationId, runScopeIdentity(run)].join("\u0001");

const scopeIntentFor = (run: Run, surfacePath: string): CommonScopeIntent => ({
  version: "CommonScopeIntent.v1",
  surfacePath,
  run: {
    inputId: run.runSettings.inputId,
    workflowId: run.runSettings.workflowId,
    settingsNameAndVersion: run.runSettings.settingsNameAndVersion,
    runAttemptId: run.runSettings.runAttemptId,
  },
});

const conversationScopeFor = (
  organizationId: string,
  run: Run,
): Promise<NucleusConversationScope> =>
  nucleusConversationScopeForRun(organizationId, scopeIntentFor(run, "/").run);

const messagesFromConversation = (
  conversation: NucleusConversationDetail,
): Message[] =>
  conversation.messages.map((message) => ({
    id: message.id,
    ...(message.turnId ? { turnId: message.turnId } : {}),
    type: message.role === "user" ? "sent" : "received",
    content:
      message.text ||
      (message.status === "cancelled"
        ? cancellationMessage
        : message.status === "failed"
          ? safeFailureMessage
          : message.text),
    timestamp: new Date(message.createdAt),
    dataType: "text",
    nucleusStatus: message.status,
    ...(message.answerStatus || message.status === "unsupported"
      ? {
          nucleusGrounding: {
            status: message.answerStatus?.traceStatus ?? "unsupported",
            reconciliationStatus:
              message.answerStatus?.reconciliationStatus ?? "unsupported",
            evidenceLabels: (message.sources ?? []).map(
              (source) => source.title,
            ),
          },
        }
      : {}),
    ...(message.sources ? { nucleusSources: message.sources } : {}),
  }));

const isCompatibilityMiss = (error: unknown): boolean =>
  error instanceof NucleusConversationClientError &&
  error.code === "UNAVAILABLE" &&
  (error.status === 404 || error.status === 405);

const defaultProbeReadiness: ReadinessProbe = async ({ streamUrl, signal }) => {
  // The caller validates a fresh authenticated session before this local
  // customer/run scope check. Remote availability is proven by the first
  // authenticated history or turn request, without a redundant health fetch.
  return !signal.aborted && isAllowedNucleusStreamUrl(streamUrl);
};

const connectionAttemptWithinTimeout = ({
  attempt,
  controller,
  timeoutMs,
}: {
  attempt: () => boolean | Promise<boolean>;
  controller: AbortController;
  timeoutMs: number;
}): Promise<boolean> =>
  new Promise<boolean>((resolve, reject) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const finish = (ready: boolean) => {
      if (settled) return;
      settled = true;
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      controller.signal.removeEventListener("abort", handleAbort);
      resolve(ready);
    };
    const fail = (error: unknown) => {
      if (settled) return;
      settled = true;
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      controller.signal.removeEventListener("abort", handleAbort);
      reject(error);
    };
    const handleAbort = () => finish(false);
    controller.signal.addEventListener("abort", handleAbort, { once: true });
    timeoutId = setTimeout(
      () => {
        fail(new NucleusConnectionTimeoutError());
        controller.abort();
      },
      Math.max(1, timeoutMs),
    );

    let readiness: boolean | Promise<boolean>;
    try {
      readiness = attempt();
    } catch (error) {
      fail(error);
      return;
    }
    void Promise.resolve(readiness).then(finish, fail);
  });

export const NucleusPlatformProvider = ({
  children,
  streamUrl = apiConfig.nucleusPlatform.streamUrl,
  getIdToken: getIdTokenOverride,
  getFreshIdToken: getFreshIdTokenOverride,
  getCurrentPath = defaultGetCurrentPath,
  createId = defaultCreateId,
  probeReadiness = defaultProbeReadiness,
  readinessTimeoutMs = defaultReadinessTimeoutMs,
  streamTurn = streamNucleusTurn,
  listConversations = listNucleusConversations,
  getConversation,
  getConversationPage,
}: {
  children: ReactNode;
  streamUrl?: string;
  getIdToken?: () => string | undefined;
  getFreshIdToken?: GetFreshIdToken;
  getCurrentPath?: () => string;
  createId?: () => string;
  probeReadiness?: ReadinessProbe;
  readinessTimeoutMs?: number;
  streamTurn?: typeof streamNucleusTurn;
  listConversations?: typeof listNucleusConversations;
  getConversation?: typeof getNucleusConversation;
  getConversationPage?: typeof getNucleusConversationPage;
}): JSX.Element => {
  const { selectedCustomerId } = useCustomer();
  const { getFreshIdToken: getFreshIdTokenFromContext, markSessionExpired } =
    useContext(IdTokenContext);
  const getFreshIdToken = useCallback(
    (options?: GetFreshIdTokenOptions): FreshIdTokenResult => {
      if (getFreshIdTokenOverride) return getFreshIdTokenOverride(options);
      if (getIdTokenOverride) return getIdTokenOverride();
      return getFreshIdTokenFromContext(options);
    },
    [getFreshIdTokenFromContext, getFreshIdTokenOverride, getIdTokenOverride],
  );
  const getRequiredFreshIdToken = useCallback(
    (options?: GetFreshIdTokenOptions): RequiredFreshIdTokenResult => {
      const requireToken = (token: string | undefined): string => {
        if (!token) throw new NucleusSessionExpiredError();
        return token;
      };
      const handleAuthenticationError = (error: unknown): never => {
        const normalized = normalizeAuthenticationError(error);
        if (normalized instanceof NucleusSessionExpiredError) {
          markSessionExpired();
        }
        throw normalized;
      };

      let tokenResult: FreshIdTokenResult;
      try {
        tokenResult = getFreshIdToken(options);
      } catch (error) {
        return handleAuthenticationError(error);
      }

      return isPromiseLike<string | undefined>(tokenResult)
        ? Promise.resolve(tokenResult)
            .then(requireToken)
            .catch(handleAuthenticationError)
        : (() => {
            try {
              return requireToken(tokenResult);
            } catch (error) {
              return handleAuthenticationError(error);
            }
          })();
    },
    [getFreshIdToken, markSessionExpired],
  );
  const runAuthenticatedRequest = useCallback(
    <T,>(
      request: (token: string) => Promise<T>,
      isUnauthorized: (error: unknown) => boolean,
      signal: AbortSignal,
    ): Promise<T> =>
      requestWithOneForcedRefresh({
        requireToken: getRequiredFreshIdToken,
        request,
        isUnauthorized,
        signal,
        tokenTimeoutMs: readinessTimeoutMs,
      }),
    [getRequiredFreshIdToken, readinessTimeoutMs],
  );
  const { mainRun, lastSuccessfulRun } = useContext(VersionedDataContext);
  const visibleRun = mainRun || lastSuccessfulRun;
  const visibleRunScopeIdentity = runScopeIdentity(visibleRun);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isAwaitingResponse, setIsAwaitingResponse] = useState(false);
  const [chatSessionId, setChatSessionId] = useState<string | null>(null);
  const [chatContext, setChatContext] = useState<string | null>(null);
  const [platformConnectionFailure, setPlatformConnectionFailure] =
    useState<ScopedPlatformConnectionFailure | null>(null);
  const [agentState, setAgentState] = useState<AgentState>(initialAgentState);
  const [isStopRequested, setIsStopRequested] = useState(false);
  const [queuedTurns, setQueuedTurns] = useState<PlatformQueuedTurn[]>([]);
  const [queuedTurnPauseReason, setQueuedTurnPauseReason] =
    useState<QueuedTurnPauseReason | null>(null);
  const [platformConversationHistory, setPlatformConversationHistory] =
    useState<PlatformConversationHistoryItem[]>([]);
  const [
    platformConversationHistoryLoading,
    setPlatformConversationHistoryLoading,
  ] = useState(false);
  const [
    platformConversationHistoryError,
    setPlatformConversationHistoryError,
  ] = useState<string | null>(null);
  const [platformEarlierMessagesLoading, setPlatformEarlierMessagesLoading] =
    useState(false);
  const [platformEarlierMessagesError, setPlatformEarlierMessagesError] =
    useState<string | null>(null);
  const readinessAbortRef = useRef<AbortController | null>(null);
  const historyAbortRef = useRef<AbortController | null>(null);
  const earlierMessagesAbortRef = useRef<AbortController | null>(null);
  const connectionAttemptRef = useRef<ConnectionAttempt | null>(null);
  const connectedScopeKeyRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  const earlierMessagesCursorRef = useRef<string | null>(null);
  const conversationHistoryKeyByIdRef = useRef(new Map<string, string>());
  const conversationHistoryByKeyRef = useRef(
    new Map<string, NucleusConversationSummary>(),
  );
  const activeCustomerIdRef = useRef<string | null>(null);
  const activeRunRef = useRef<Run | null>(null);
  const activeSurfacePathRef = useRef<string | null>(null);
  const activeContextRef = useRef<string | null>(null);
  const activeCancellationRef = useRef<(() => void) | null>(null);
  const activeTurnRef = useRef<PlatformQueuedTurn | null>(null);
  const queuedTurnsRef = useRef<PlatformQueuedTurn[]>([]);
  const queuedTurnPauseReasonRef = useRef<QueuedTurnPauseReason | null>(null);
  const dispatchQueuedTurnRef = useRef<
    ((turn: PlatformQueuedTurn) => boolean) | null
  >(null);
  const queueDrainScheduledRef = useRef(false);
  const currentViewContextRef = useRef<RegisteredViewContext | null>(null);
  const currentViewDataRef = useRef<RegisteredCurrentViewData | null>(null);
  const currentViewDataCacheRef = useRef(
    new Map<string, RegisteredCurrentViewData>(),
  );
  const activeMetricContextRef = useRef<RegisteredActiveMetricContext | null>(
    null,
  );
  const inFlightRef = useRef(false);
  const generationRef = useRef(0);
  const visibleScopeKeyRef = useRef<string | null>(null);

  const replaceQueuedTurns = useCallback((next: PlatformQueuedTurn[]) => {
    queuedTurnsRef.current = next;
    setQueuedTurns(next);
  }, []);

  const clearQueuedTurns = useCallback(() => {
    replaceQueuedTurns([]);
  }, [replaceQueuedTurns]);

  const pauseQueuedTurns = useCallback((reason: QueuedTurnPauseReason) => {
    queuedTurnPauseReasonRef.current = reason;
    setQueuedTurnPauseReason(reason);
  }, []);

  const clearQueuedTurnPause = useCallback(() => {
    queuedTurnPauseReasonRef.current = null;
    setQueuedTurnPauseReason(null);
  }, []);

  const scheduleQueuedTurnDispatch = useCallback(() => {
    if (
      queueDrainScheduledRef.current ||
      queuedTurnPauseReasonRef.current !== null
    ) {
      return;
    }
    queueDrainScheduledRef.current = true;
    void Promise.resolve().then(() => {
      queueDrainScheduledRef.current = false;
      if (
        queuedTurnPauseReasonRef.current !== null ||
        inFlightRef.current ||
        activeTurnRef.current ||
        earlierMessagesAbortRef.current !== null
      ) {
        return;
      }
      const [nextTurn, ...remaining] = queuedTurnsRef.current;
      if (!nextTurn) return;
      if (dispatchQueuedTurnRef.current?.(nextTurn) === true) {
        replaceQueuedTurns(remaining);
      }
    });
  }, [replaceQueuedTurns]);

  const cancelQueuedPrompt = useCallback(
    (id: string) => {
      const remaining = queuedTurnsRef.current.filter((turn) => turn.id !== id);
      replaceQueuedTurns(remaining);
      if (
        remaining.length === 0 &&
        queuedTurnPauseReasonRef.current === "authentication_unavailable"
      ) {
        clearQueuedTurnPause();
      }
    },
    [clearQueuedTurnPause, replaceQueuedTurns],
  );

  const retryQueuedDispatch = useCallback(() => {
    if (
      queuedTurnPauseReasonRef.current !== "authentication_unavailable" ||
      inFlightRef.current ||
      activeTurnRef.current ||
      earlierMessagesAbortRef.current !== null ||
      platformEarlierMessagesLoading ||
      queuedTurnsRef.current.length === 0
    ) {
      return;
    }
    clearQueuedTurnPause();
    scheduleQueuedTurnDispatch();
  }, [
    clearQueuedTurnPause,
    platformEarlierMessagesLoading,
    scheduleQueuedTurnDispatch,
  ]);

  const visibleRunOrganizationId =
    visibleRun?.runSettings.organizationId ?? null;
  const visibleOrganizationId =
    visibleRun &&
    (!selectedCustomerId || selectedCustomerId === visibleRunOrganizationId)
      ? (selectedCustomerId ?? visibleRunOrganizationId)
      : null;
  const visibleConnectionScopeKey =
    visibleRun && visibleOrganizationId
      ? connectionScopeIdentity({
          organizationId: visibleOrganizationId,
          run: visibleRun,
        })
      : null;
  visibleScopeKeyRef.current = visibleConnectionScopeKey;
  const currentViewOrganizationId =
    !selectedCustomerId || selectedCustomerId === visibleRunOrganizationId
      ? visibleRunOrganizationId
      : null;
  const currentViewDataCacheKey = useCallback(
    (surfaceId: string, viewContext: CommonViewContextV1): string =>
      JSON.stringify({
        organizationId: currentViewOrganizationId,
        runScopeIdentity: visibleRunScopeIdentity,
        surfaceId,
        viewContext,
      }),
    [currentViewOrganizationId, visibleRunScopeIdentity],
  );
  const registerCurrentViewContext = useCallback(
    (viewContext: CommonViewContextV1): (() => void) => {
      const registration = {};
      currentViewContextRef.current = {
        registration,
        organizationId: currentViewOrganizationId,
        viewContext,
      };
      return () => {
        if (currentViewContextRef.current?.registration === registration) {
          currentViewContextRef.current = null;
        }
      };
    },
    [currentViewOrganizationId],
  );
  const registerCurrentViewData = useCallback(
    (
      data: CommonCurrentViewDataV1,
      viewContext: CommonViewContextV1,
    ): (() => void) => {
      if (
        !isCommonCurrentViewDataReceipt(data) ||
        data.surfaceId !== viewContext.surfaceId
      ) {
        currentViewDataRef.current = null;
        return () => {};
      }
      const registration = {};
      const registered = {
        registration,
        organizationId: currentViewOrganizationId,
        runScopeIdentity: visibleRunScopeIdentity,
        data,
        viewContext,
      };
      currentViewDataRef.current = registered;
      currentViewDataCacheRef.current.set(
        currentViewDataCacheKey(data.surfaceId, viewContext),
        registered,
      );
      return () => {
        if (currentViewDataRef.current?.registration === registration) {
          currentViewDataRef.current = null;
        }
      };
    },
    [
      currentViewDataCacheKey,
      currentViewOrganizationId,
      visibleRunScopeIdentity,
    ],
  );
  const registerActiveMetricContext = useCallback(
    (
      metricContext: CommonMetricContext,
      viewContext: CommonViewContextV1,
    ): (() => void) => {
      if (metricContext.surfaceId !== viewContext.surfaceId) {
        activeMetricContextRef.current = null;
        return () => {};
      }
      const registration = {};
      activeMetricContextRef.current = {
        registration,
        organizationId: currentViewOrganizationId,
        metricContext,
        viewContext,
      };
      return () => {
        if (activeMetricContextRef.current?.registration === registration) {
          activeMetricContextRef.current = null;
        }
      };
    },
    [currentViewOrganizationId],
  );

  const loadConversationPage = useCallback(
    async (
      input: Parameters<typeof getNucleusConversationPage>[0],
    ): Promise<NucleusConversationDetailPage> => {
      if (getConversationPage) return getConversationPage(input);
      if (getConversation) {
        return { conversation: await getConversation(input) };
      }
      return getNucleusConversationPage(input);
    },
    [getConversation, getConversationPage],
  );

  const listConversationsForScope = useCallback(
    async ({
      organizationId,
      expectedScope,
      signal,
    }: {
      organizationId: string;
      expectedScope: NucleusConversationScope;
      signal: AbortSignal;
    }): Promise<NucleusConversationSummary[]> => {
      const byId = new Map<string, NucleusConversationSummary>();
      const seenCursors = new Set<string>();
      let cursor: string | undefined;
      let exhausted = false;

      for (let page = 0; page < maxConversationHistoryPages; page += 1) {
        const pageCursor = cursor;
        const listed = await runAuthenticatedRequest(
          (token) =>
            listConversations({
              streamUrl,
              token,
              organizationId,
              limit: 50,
              ...(expectedScope.runRef ? { runRef: expectedScope.runRef } : {}),
              ...(pageCursor ? { cursor: pageCursor } : {}),
              signal,
            }),
          (error) => !signal.aborted && isConversationUnauthorized(error),
          signal,
        );
        for (const conversation of listed.conversations) {
          if (
            nucleusConversationScopesMatch(conversation.scope, expectedScope)
          ) {
            byId.set(conversation.id, conversation);
          }
        }

        const nextCursor = listed.nextCursor;
        if (!nextCursor) {
          exhausted = true;
          break;
        }
        if (seenCursors.has(nextCursor)) {
          throw new NucleusConversationClientError(
            "INVALID_RESPONSE",
            "Nucleus returned an invalid conversation response.",
          );
        }
        seenCursors.add(nextCursor);
        cursor = nextCursor;
      }

      if (!exhausted) {
        throw new NucleusConversationClientError(
          "INVALID_RESPONSE",
          "Nucleus returned too many conversation history pages.",
        );
      }

      return [...byId.values()].sort(
        (left, right) =>
          Date.parse(right.updatedAt) - Date.parse(left.updatedAt),
      );
    },
    [listConversations, runAuthenticatedRequest, streamUrl],
  );

  const resetPlatformConversationHistory = useCallback(() => {
    historyAbortRef.current?.abort();
    historyAbortRef.current = null;
    conversationHistoryKeyByIdRef.current.clear();
    conversationHistoryByKeyRef.current.clear();
    setPlatformConversationHistory([]);
    setPlatformConversationHistoryLoading(false);
    setPlatformConversationHistoryError(null);
  }, []);

  const refreshPlatformConversationHistory =
    useCallback(async (): Promise<boolean> => {
      const organizationId = activeCustomerIdRef.current;
      const run = activeRunRef.current;
      const connectedScopeKey = connectedScopeKeyRef.current;
      if (
        !connectedScopeKey ||
        visibleScopeKeyRef.current !== connectedScopeKey ||
        !organizationId ||
        !run
      ) {
        resetPlatformConversationHistory();
        return false;
      }

      const generation = generationRef.current;
      const expectedRunScopeIdentity = runScopeIdentity(run);
      historyAbortRef.current?.abort();
      const controller = new AbortController();
      historyAbortRef.current = controller;
      setPlatformConversationHistoryLoading(true);
      setPlatformConversationHistoryError(null);

      try {
        const expectedScope = await conversationScopeFor(organizationId, run);
        const scoped = await listConversationsForScope({
          organizationId,
          expectedScope,
          signal: controller.signal,
        });
        if (
          controller.signal.aborted ||
          generationRef.current !== generation ||
          connectedScopeKeyRef.current !== connectedScopeKey ||
          visibleScopeKeyRef.current !== connectedScopeKey ||
          activeCustomerIdRef.current !== organizationId ||
          runScopeIdentity(activeRunRef.current) !== expectedRunScopeIdentity
        ) {
          return false;
        }

        const previousKeys = conversationHistoryKeyByIdRef.current;
        const nextKeys = new Map<string, string>();
        const nextByKey = new Map<string, NucleusConversationSummary>();
        const projected = scoped.map((conversation) => {
          const key =
            previousKeys.get(conversation.id) ?? `history-${createId()}`;
          nextKeys.set(conversation.id, key);
          nextByKey.set(key, conversation);
          return {
            key,
            title: conversation.title,
            ...(conversation.preview ? { preview: conversation.preview } : {}),
            updatedAt: conversation.updatedAt,
            state: conversation.state,
            canContinue: conversation.canContinue,
            isCurrent: conversation.id === conversationIdRef.current,
          } satisfies PlatformConversationHistoryItem;
        });
        conversationHistoryKeyByIdRef.current = nextKeys;
        conversationHistoryByKeyRef.current = nextByKey;
        setPlatformConversationHistory(projected);
        return true;
      } catch (error) {
        if (
          !controller.signal.aborted &&
          generationRef.current === generation &&
          !(error instanceof NucleusSessionExpiredError)
        ) {
          setPlatformConversationHistoryError(
            authenticationFailureMessage(error) ??
              (isCompatibilityMiss(error)
                ? "Conversation history isn't available yet."
                : "Conversation history is temporarily unavailable."),
          );
        }
        return false;
      } finally {
        if (historyAbortRef.current === controller) {
          historyAbortRef.current = null;
          setPlatformConversationHistoryLoading(false);
        }
      }
    }, [createId, listConversationsForScope, resetPlatformConversationHistory]);

  const resumePlatformConversation = useCallback(
    async (key: string): Promise<boolean> => {
      const summary = conversationHistoryByKeyRef.current.get(key);
      const organizationId = activeCustomerIdRef.current;
      const run = activeRunRef.current;
      const connectedScopeKey = connectedScopeKeyRef.current;
      if (
        !summary ||
        !summary.canContinue ||
        summary.state === "running" ||
        !connectedScopeKey ||
        visibleScopeKeyRef.current !== connectedScopeKey ||
        !organizationId ||
        !run ||
        inFlightRef.current ||
        queuedTurnPauseReasonRef.current !== null ||
        queuedTurnsRef.current.length > 0 ||
        (readinessAbortRef.current !== null &&
          !readinessAbortRef.current.signal.aborted)
      ) {
        return false;
      }

      const startingGeneration = generationRef.current;
      const expectedScope = await conversationScopeFor(
        organizationId,
        run,
      ).catch(() => undefined);
      if (
        generationRef.current !== startingGeneration ||
        connectedScopeKeyRef.current !== connectedScopeKey ||
        visibleScopeKeyRef.current !== connectedScopeKey ||
        activeCustomerIdRef.current !== organizationId ||
        runScopeIdentity(activeRunRef.current) !== runScopeIdentity(run) ||
        !expectedScope ||
        !nucleusConversationScopesMatch(summary.scope, expectedScope)
      ) {
        setPlatformConversationHistoryError(
          "That conversation isn't available for this customer and run.",
        );
        return false;
      }

      const expectedRunScopeIdentity = runScopeIdentity(run);
      const generation = startingGeneration + 1;
      generationRef.current = generation;
      historyAbortRef.current?.abort();
      earlierMessagesAbortRef.current?.abort();
      const controller = new AbortController();
      historyAbortRef.current = controller;
      setPlatformConversationHistoryError(null);
      setIsConnecting(true);

      try {
        const loaded = await runAuthenticatedRequest(
          (token) =>
            loadConversationPage({
              streamUrl,
              token,
              conversationId: summary.id,
              expectedScope,
              signal: controller.signal,
            }),
          (error) =>
            !controller.signal.aborted && isConversationUnauthorized(error),
          controller.signal,
        );
        const conversation = loaded.conversation;
        if (
          controller.signal.aborted ||
          generationRef.current !== generation ||
          conversation.id !== summary.id ||
          connectedScopeKeyRef.current !== connectedScopeKey ||
          visibleScopeKeyRef.current !== connectedScopeKey ||
          activeCustomerIdRef.current !== organizationId ||
          runScopeIdentity(activeRunRef.current) !== expectedRunScopeIdentity
        ) {
          return false;
        }

        conversationIdRef.current = conversation.id;
        earlierMessagesCursorRef.current = loaded.nextCursor ?? null;
        conversationHistoryByKeyRef.current.set(key, conversation);
        setMessages(messagesFromConversation(conversation));
        setChatSessionId(conversation.id);
        setAgentState(initialAgentState);
        setIsAwaitingResponse(false);
        setIsStopRequested(false);
        setPlatformEarlierMessagesLoading(false);
        setPlatformEarlierMessagesError(null);
        setPlatformConversationHistory((previous) =>
          previous.map((item) => ({
            ...item,
            isCurrent: item.key === key,
          })),
        );
        return true;
      } catch (error) {
        if (
          !controller.signal.aborted &&
          generationRef.current === generation &&
          !(error instanceof NucleusSessionExpiredError)
        ) {
          setPlatformConversationHistoryError(
            authenticationFailureMessage(error) ??
              "That conversation couldn't be opened. Please try again.",
          );
        }
        return false;
      } finally {
        if (historyAbortRef.current === controller) {
          historyAbortRef.current = null;
        }
        if (generationRef.current === generation) setIsConnecting(false);
      }
    },
    [loadConversationPage, runAuthenticatedRequest, streamUrl],
  );

  const loadEarlierPlatformConversationMessages =
    useCallback(async (): Promise<boolean> => {
      const cursor = earlierMessagesCursorRef.current;
      const conversationId = conversationIdRef.current;
      const organizationId = activeCustomerIdRef.current;
      const run = activeRunRef.current;
      const connectedScopeKey = connectedScopeKeyRef.current;
      if (
        !cursor ||
        !conversationId ||
        !organizationId ||
        !run ||
        !connectedScopeKey ||
        visibleScopeKeyRef.current !== connectedScopeKey ||
        platformEarlierMessagesLoading ||
        inFlightRef.current ||
        activeTurnRef.current !== null ||
        queuedTurnPauseReasonRef.current !== null ||
        queuedTurnsRef.current.length > 0 ||
        earlierMessagesAbortRef.current !== null ||
        (readinessAbortRef.current !== null &&
          !readinessAbortRef.current.signal.aborted)
      ) {
        return false;
      }

      const startingGeneration = generationRef.current;
      const expectedRunScopeIdentity = runScopeIdentity(run);
      const controller = new AbortController();
      earlierMessagesAbortRef.current = controller;
      setPlatformEarlierMessagesLoading(true);
      setPlatformEarlierMessagesError(null);
      try {
        const expectedScope = await conversationScopeFor(
          organizationId,
          run,
        ).catch(() => undefined);
        if (
          !expectedScope ||
          controller.signal.aborted ||
          generationRef.current !== startingGeneration ||
          connectedScopeKeyRef.current !== connectedScopeKey ||
          visibleScopeKeyRef.current !== connectedScopeKey ||
          conversationIdRef.current !== conversationId ||
          activeCustomerIdRef.current !== organizationId ||
          runScopeIdentity(activeRunRef.current) !== expectedRunScopeIdentity ||
          inFlightRef.current ||
          activeTurnRef.current !== null ||
          queuedTurnPauseReasonRef.current !== null ||
          queuedTurnsRef.current.length > 0
        ) {
          return false;
        }

        const loaded = await runAuthenticatedRequest(
          (token) =>
            loadConversationPage({
              streamUrl,
              token,
              conversationId,
              expectedScope,
              cursor,
              signal: controller.signal,
            }),
          (error) =>
            !controller.signal.aborted && isConversationUnauthorized(error),
          controller.signal,
        );
        if (
          controller.signal.aborted ||
          generationRef.current !== startingGeneration ||
          connectedScopeKeyRef.current !== connectedScopeKey ||
          visibleScopeKeyRef.current !== connectedScopeKey ||
          conversationIdRef.current !== conversationId ||
          loaded.conversation.id !== conversationId ||
          activeCustomerIdRef.current !== organizationId ||
          runScopeIdentity(activeRunRef.current) !== expectedRunScopeIdentity
        ) {
          return false;
        }
        if (loaded.nextCursor === cursor) {
          throw new NucleusConversationClientError(
            "INVALID_RESPONSE",
            "Nucleus returned an invalid conversation response.",
          );
        }

        const olderMessages = messagesFromConversation(loaded.conversation);
        setMessages((current) => {
          const existingIds = new Set(
            current
              .map((message) => message.id)
              .filter((id): id is string => typeof id === "string"),
          );
          return [
            ...olderMessages.filter(
              (message) => !message.id || !existingIds.has(message.id),
            ),
            ...current,
          ];
        });
        earlierMessagesCursorRef.current = loaded.nextCursor ?? null;
        return true;
      } catch (error) {
        if (
          !controller.signal.aborted &&
          generationRef.current === startingGeneration &&
          !(error instanceof NucleusSessionExpiredError)
        ) {
          setPlatformEarlierMessagesError(
            authenticationFailureMessage(error) ??
              "Earlier messages couldn't be loaded. Please try again.",
          );
        }
        return false;
      } finally {
        if (earlierMessagesAbortRef.current === controller) {
          earlierMessagesAbortRef.current = null;
          setPlatformEarlierMessagesLoading(false);
          if (
            queuedTurnPauseReasonRef.current === null &&
            queuedTurnsRef.current.length > 0
          ) {
            scheduleQueuedTurnDispatch();
          }
        }
      }
    }, [
      loadConversationPage,
      platformEarlierMessagesLoading,
      runAuthenticatedRequest,
      scheduleQueuedTurnDispatch,
      streamUrl,
    ]);

  const appendMessage = useCallback((message: Message) => {
    setMessages((previous) => [...previous, message]);
  }, []);

  const disconnectChat = useCallback(() => {
    generationRef.current += 1;
    connectionAttemptRef.current = null;
    readinessAbortRef.current?.abort();
    readinessAbortRef.current = null;
    resetPlatformConversationHistory();
    earlierMessagesAbortRef.current?.abort();
    earlierMessagesAbortRef.current = null;
    connectedScopeKeyRef.current = null;
    abortRef.current?.abort();
    abortRef.current = null;
    conversationIdRef.current = null;
    earlierMessagesCursorRef.current = null;
    activeCustomerIdRef.current = null;
    activeRunRef.current = null;
    activeSurfacePathRef.current = null;
    activeContextRef.current = null;
    activeCancellationRef.current = null;
    activeTurnRef.current = null;
    currentViewDataRef.current = null;
    currentViewDataCacheRef.current.clear();
    activeMetricContextRef.current = null;
    inFlightRef.current = false;
    clearQueuedTurns();
    clearQueuedTurnPause();
    setIsConnected(false);
    setIsConnecting(false);
    setMessages([]);
    setIsAwaitingResponse(false);
    setChatSessionId(null);
    setChatContext(null);
    setPlatformConnectionFailure(null);
    setAgentState(initialAgentState);
    setIsStopRequested(false);
    setPlatformEarlierMessagesLoading(false);
    setPlatformEarlierMessagesError(null);
  }, [
    clearQueuedTurnPause,
    clearQueuedTurns,
    resetPlatformConversationHistory,
  ]);

  const startNewConversation = useCallback(async (): Promise<boolean> => {
    const organizationId = activeCustomerIdRef.current;
    const run = activeRunRef.current;
    const connectedScopeKey = connectedScopeKeyRef.current;
    if (
      !connectedScopeKey ||
      visibleScopeKeyRef.current !== connectedScopeKey ||
      !organizationId ||
      !run ||
      !activeContextRef.current ||
      (readinessAbortRef.current !== null &&
        !readinessAbortRef.current.signal.aborted)
    ) {
      return false;
    }

    const generation = generationRef.current + 1;
    generationRef.current = generation;
    clearQueuedTurns();
    clearQueuedTurnPause();
    abortRef.current?.abort();
    abortRef.current = null;
    earlierMessagesAbortRef.current?.abort();
    earlierMessagesAbortRef.current = null;
    activeCancellationRef.current = null;
    activeTurnRef.current = null;
    inFlightRef.current = false;
    setIsAwaitingResponse(false);
    setIsStopRequested(false);
    const controller = new AbortController();
    readinessAbortRef.current?.abort();
    readinessAbortRef.current = controller;
    setIsConnecting(true);
    try {
      if (generationRef.current !== generation || controller.signal.aborted) {
        return false;
      }
      const conversationId = `conversation-${createId()}`;
      conversationIdRef.current = conversationId;
      earlierMessagesCursorRef.current = null;
      setPlatformConversationHistory((previous) =>
        previous.map((item) => ({ ...item, isCurrent: false })),
      );
      setMessages([]);
      setChatSessionId(conversationId);
      setAgentState(initialAgentState);
      setPlatformEarlierMessagesLoading(false);
      setPlatformEarlierMessagesError(null);
      return true;
    } catch {
      return false;
    } finally {
      if (generationRef.current === generation) {
        readinessAbortRef.current = null;
        setIsConnecting(false);
      }
    }
  }, [clearQueuedTurnPause, clearQueuedTurns, createId]);

  const connectChat = useCallback(
    async (run: Run | null, context: string): Promise<boolean> => {
      if (!run || !isAllowedNucleusStreamUrl(streamUrl)) {
        disconnectChat();
        return false;
      }
      if (
        selectedCustomerId &&
        run.runSettings.organizationId !== selectedCustomerId
      ) {
        disconnectChat();
        return false;
      }

      const organizationId =
        selectedCustomerId ?? run.runSettings.organizationId;
      if (!organizationId) {
        disconnectChat();
        return false;
      }

      const surfacePath = normalizeSurfacePath(getCurrentPath());
      const scopeKey = connectionScopeIdentity({
        organizationId,
        run,
      });

      if (
        connectedScopeKeyRef.current === scopeKey &&
        conversationIdRef.current
      ) {
        activeSurfacePathRef.current = surfacePath;
        if (activeContextRef.current !== context) {
          activeContextRef.current = context;
          setChatContext(context);
        }
        return true;
      }

      const currentAttempt = connectionAttemptRef.current;
      if (
        currentAttempt?.key === scopeKey &&
        !currentAttempt.controller.signal.aborted
      ) {
        currentAttempt.context = context;
        currentAttempt.surfacePath = surfacePath;
        return currentAttempt.promise;
      }

      if (connectedScopeKeyRef.current || conversationIdRef.current) {
        // A tenant/run change must synchronously invalidate the old scope and
        // cancel its turn before probing the replacement.
        disconnectChat();
      } else {
        generationRef.current += 1;
        connectionAttemptRef.current = null;
        readinessAbortRef.current?.abort();
        readinessAbortRef.current = null;
      }

      const generation = generationRef.current + 1;
      generationRef.current = generation;
      const controller = new AbortController();
      readinessAbortRef.current = controller;
      setIsConnecting(true);
      setPlatformConnectionFailure(null);

      const clearFailedAttempt = (error?: unknown): false => {
        if (generationRef.current === generation) {
          connectionAttemptRef.current = null;
          readinessAbortRef.current = null;
          setIsConnecting(false);
          const failure = retryablePlatformFailure(error);
          setPlatformConnectionFailure(
            failure ? { ...failure, scopeKey } : null,
          );
        }
        return false;
      };
      const finishReadiness = async (
        readiness: Promise<boolean>,
      ): Promise<boolean> => {
        try {
          const ready = await readiness;
          if (generationRef.current !== generation) return false;
          const attempt = connectionAttemptRef.current;
          if (attempt?.generation !== generation) return false;
          if (controller.signal.aborted || !ready) {
            connectionAttemptRef.current = null;
            readinessAbortRef.current = null;
            setIsConnecting(false);
            return false;
          }

          if (generationRef.current !== generation) return false;
          const latestAttempt = connectionAttemptRef.current;
          if (latestAttempt?.generation !== generation) return false;
          connectionAttemptRef.current = null;
          readinessAbortRef.current = null;
          if (controller.signal.aborted) return false;

          // A new thread does not need a server round trip. The authenticated
          // turn endpoint adopts and persists this opaque ID on first use;
          // saved threads remain server-authorized through explicit history.
          const conversationId = `conversation-${createId()}`;
          const resolvedContext = latestAttempt.context;
          connectedScopeKeyRef.current = scopeKey;
          conversationIdRef.current = conversationId;
          earlierMessagesCursorRef.current = null;
          activeCustomerIdRef.current = organizationId;
          activeRunRef.current = run;
          activeSurfacePathRef.current = latestAttempt.surfacePath;
          activeContextRef.current = resolvedContext;
          setMessages([]);
          setAgentState(initialAgentState);
          setIsAwaitingResponse(false);
          setIsStopRequested(false);
          setPlatformEarlierMessagesLoading(false);
          setPlatformEarlierMessagesError(null);
          setChatContext(resolvedContext);
          setChatSessionId(conversationId);
          setIsConnecting(false);
          setIsConnected(true);
          setPlatformConnectionFailure(null);
          return true;
        } catch (error) {
          return clearFailedAttempt(error);
        }
      };
      const beginReadiness = (): boolean | Promise<boolean> => {
        if (controller.signal.aborted) return false;
        // A fresh session plus this local scope check form one bounded Ready
        // invariant. No token is sent because this probe performs no I/O.
        return probeReadiness({
          streamUrl,
          signal: controller.signal,
        });
      };

      const promise = finishReadiness(
        connectionAttemptWithinTimeout({
          attempt: () => {
            const tokenResult = getRequiredFreshIdToken();
            return isPromiseLike<string>(tokenResult)
              ? Promise.resolve(tokenResult).then(() => beginReadiness())
              : beginReadiness();
          },
          controller,
          timeoutMs: readinessTimeoutMs,
        }),
      );
      connectionAttemptRef.current = {
        context,
        controller,
        generation,
        key: scopeKey,
        organizationId,
        promise,
        run,
        surfacePath,
      };
      return promise;
    },
    [
      disconnectChat,
      createId,
      getRequiredFreshIdToken,
      getCurrentPath,
      probeReadiness,
      readinessTimeoutMs,
      selectedCustomerId,
      streamUrl,
    ],
  );

  const createPlatformTurn = useCallback(
    (
      content: string,
      metricContext?: CommonMetricContext,
      viewContext?: CommonViewContextV1,
    ): PlatformQueuedTurn | undefined => {
      const text = content.trim();
      const conversationId = conversationIdRef.current;
      const organizationId = activeCustomerIdRef.current;
      const run = activeRunRef.current;
      const scopeKey = connectedScopeKeyRef.current;
      const surfacePath = normalizeSurfacePath(getCurrentPath());
      if (
        !text ||
        !conversationId ||
        !organizationId ||
        !run ||
        !scopeKey ||
        !activeContextRef.current ||
        visibleScopeKeyRef.current !== scopeKey ||
        (readinessAbortRef.current !== null &&
          !readinessAbortRef.current.signal.aborted)
      ) {
        return undefined;
      }

      const registeredViewContext = currentViewContextRef.current;
      const effectiveViewContext =
        viewContext ??
        (registeredViewContext?.organizationId === organizationId
          ? registeredViewContext.viewContext
          : undefined);
      const registeredActiveMetricContext = activeMetricContextRef.current;
      const effectiveMetricContext =
        metricContext ??
        (effectiveViewContext !== undefined &&
        registeredActiveMetricContext?.organizationId === organizationId &&
        surfacePath ===
          `/${registeredActiveMetricContext.metricContext.surfaceId}` &&
        sameViewContext(
          registeredActiveMetricContext.viewContext,
          effectiveViewContext,
        )
          ? registeredActiveMetricContext.metricContext
          : undefined);
      const requestedRunScopeIdentity = runScopeIdentity(run);
      if (!requestedRunScopeIdentity) return undefined;
      const currentViewDataCandidate = currentViewDataRef.current;
      const cachedCurrentViewData = effectiveViewContext
        ? currentViewDataCacheRef.current.get(
            currentViewDataCacheKey(
              effectiveViewContext.surfaceId,
              effectiveViewContext,
            ),
          )
        : undefined;
      const registeredCurrentViewData =
        currentViewDataCandidate?.organizationId === organizationId &&
        currentViewDataCandidate.runScopeIdentity ===
          requestedRunScopeIdentity &&
        effectiveViewContext !== undefined &&
        currentViewSurfacePathMatches(
          currentViewDataCandidate.data.surfaceId,
          surfacePath,
        ) &&
        sameViewContext(
          currentViewDataCandidate.viewContext,
          effectiveViewContext,
        )
          ? currentViewDataCandidate
          : cachedCurrentViewData;
      const effectiveCurrentViewData =
        effectiveViewContext !== undefined &&
        registeredCurrentViewData?.organizationId === organizationId &&
        registeredCurrentViewData.runScopeIdentity ===
          requestedRunScopeIdentity &&
        currentViewSurfacePathMatches(
          registeredCurrentViewData.data.surfaceId,
          surfacePath,
        ) &&
        sameViewContext(
          registeredCurrentViewData.viewContext,
          effectiveViewContext,
        )
          ? registeredCurrentViewData.data
          : undefined;
      const requestId = `request-${createId()}`;
      const userMessageId = `message-${createId()}`;
      const request: EmbeddedTurnRequest = {
        version: "EmbeddedTurnRequest.v1",
        requestId,
        conversationId,
        organizationId,
        message: {
          id: userMessageId,
          text,
        },
        ...(effectiveMetricContext
          ? { metricContext: effectiveMetricContext }
          : {}),
        // Descriptive UI selection metadata only. The server must never use
        // this field to authorize a tenant, run, receipt, or data access.
        ...(effectiveViewContext ? { viewContext: effectiveViewContext } : {}),
        ...(effectiveCurrentViewData
          ? { currentViewData: effectiveCurrentViewData }
          : {}),
        // This is untrusted navigation/run intent. Server-side authorization
        // and scope resolution remain authoritative.
        scopeIntent: {
          version: "CommonScopeIntent.v1",
          surfacePath,
          run: {
            inputId: run.runSettings.inputId,
            workflowId: run.runSettings.workflowId,
            settingsNameAndVersion: run.runSettings.settingsNameAndVersion,
            runAttemptId: run.runSettings.runAttemptId,
          },
        },
      };

      return {
        id: userMessageId,
        dedupeKey: queuedTurnDedupeKey(request),
        conversationId,
        organizationId,
        runScopeIdentity: requestedRunScopeIdentity,
        scopeKey,
        surfacePath,
        request,
      };
    },
    [createId, currentViewDataCacheKey, getCurrentPath],
  );

  const dispatchPlatformTurn = useCallback(
    (turn: PlatformQueuedTurn): boolean => {
      if (
        inFlightRef.current ||
        activeTurnRef.current ||
        queuedTurnPauseReasonRef.current !== null ||
        conversationIdRef.current !== turn.conversationId ||
        activeCustomerIdRef.current !== turn.organizationId ||
        runScopeIdentity(activeRunRef.current) !== turn.runScopeIdentity ||
        connectedScopeKeyRef.current !== turn.scopeKey ||
        visibleScopeKeyRef.current !== turn.scopeKey ||
        earlierMessagesAbortRef.current !== null ||
        (readinessAbortRef.current !== null &&
          !readinessAbortRef.current.signal.aborted)
      ) {
        return false;
      }

      inFlightRef.current = true;
      activeTurnRef.current = turn;
      activeSurfacePathRef.current = turn.surfacePath;
      const generation = generationRef.current + 1;
      generationRef.current = generation;
      const controller = new AbortController();
      abortRef.current?.abort();
      abortRef.current = controller;
      const evidenceLabelsById = new Map<string, string>();
      const evidenceSourcesById = new Map<string, CustomerEvidenceReference>();
      let resultSnapshot: ResultSnapshot | undefined;
      let streamedText = "";
      let terminalSeen = false;
      let cancellationRendered = false;
      let responseMessageId: string | undefined;
      let activeTurnId: string | undefined;
      let streamEventSeen = false;

      appendMessage({
        id: turn.request.message.id,
        type: "sent",
        content: turn.request.message.text,
        timestamp: new Date(),
      });
      setIsAwaitingResponse(true);
      setIsStopRequested(false);
      setAgentState(initialAgentState);

      const settleActiveTurn = (dispatchNext: boolean) => {
        if (activeTurnRef.current?.id !== turn.id) return;
        inFlightRef.current = false;
        activeTurnRef.current = null;
        activeCancellationRef.current = null;
        setIsAwaitingResponse(false);
        setIsStopRequested(false);
        if (dispatchNext) scheduleQueuedTurnDispatch();
      };

      const renderCancelledTurn = () => {
        if (cancellationRendered) return;
        cancellationRendered = true;
        const partialText = streamedText.trim();
        appendMessage({
          ...(responseMessageId ? { id: responseMessageId } : {}),
          ...(activeTurnId ? { turnId: activeTurnId } : {}),
          type: "received",
          content: partialText
            ? `${partialText}\n\n${cancellationMessage}`
            : cancellationMessage,
          timestamp: new Date(),
          dataType: "text",
          nucleusStatus: "cancelled",
        });
        setAgentState(initialAgentState);
      };

      const closeLocallyAbortedTurn = () => {
        if (terminalSeen) return;
        renderCancelledTurn();
        pauseQueuedTurns("local_abort");
        setIsAwaitingResponse(false);
        setIsStopRequested(false);
      };

      const closeAuthoritativelyCancelledTurn = () => {
        if (terminalSeen) return;
        terminalSeen = true;
        renderCancelledTurn();
        clearQueuedTurnPause();
        settleActiveTurn(true);
      };
      activeCancellationRef.current = closeLocallyAbortedTurn;

      const handleEvent = (event: NucleusTurnEvent) => {
        if (generationRef.current !== generation || terminalSeen) return;
        streamEventSeen = true;

        if (event.type === "turn.started") {
          responseMessageId = event.messageId;
          activeTurnId = event.turnId;
          setAgentState({
            ...initialAgentState,
            status: "streaming",
            workSteps: [],
            answerStarted: false,
            turnPhases: {
              understand: true,
              gather: false,
              analyze: false,
              answer: false,
            },
          });
          return;
        }

        if (event.type === "work.updated") {
          const status =
            event.status === "started"
              ? "active"
              : event.status === "completed"
                ? "complete"
                : "failed";
          setAgentState((previous) => {
            const existingSteps = previous.workSteps ?? [];
            const existingIndex = existingSteps.findIndex(
              (step) => step.id === event.workId,
            );
            const nextStep = {
              id: event.workId,
              label: event.label,
              status,
            } as const;
            const workSteps =
              existingIndex === -1
                ? [...existingSteps, nextStep]
                : existingSteps.map((step, index) =>
                    index === existingIndex ? nextStep : step,
                  );
            const shouldRecordReasoning = event.status === "started";
            return {
              ...previous,
              status: "streaming",
              workSteps,
              ...(shouldRecordReasoning
                ? {
                    reasoning: event.label,
                    reasoningHistory:
                      previous.reasoningHistory.at(-1)?.text === event.label
                        ? previous.reasoningHistory
                        : [
                            ...previous.reasoningHistory,
                            {
                              text: event.label,
                              iteration: previous.reasoningHistory.length + 1,
                            },
                          ],
                  }
                : {}),
            };
          });
          return;
        }

        if (event.type === "text.delta") {
          streamedText += event.delta;
          setAgentState((previous) => ({
            ...previous,
            status: "streaming",
            streamedText,
            answerStarted: true,
            turnPhases: {
              understand: previous.turnPhases?.understand ?? false,
              gather: previous.turnPhases?.gather ?? false,
              analyze: previous.turnPhases?.analyze ?? false,
              answer: true,
            },
          }));
          return;
        }

        if (event.type === "evidence.available") {
          setAgentState((previous) => ({
            ...previous,
            turnPhases: {
              understand: previous.turnPhases?.understand ?? false,
              gather: true,
              analyze: previous.turnPhases?.analyze ?? false,
              answer: previous.turnPhases?.answer ?? false,
            },
          }));
          const evidenceId = event.evidence.id;
          const label = safeEvidenceLabel(event.label);
          if (typeof evidenceId === "string" && evidenceId && label) {
            evidenceLabelsById.set(evidenceId, label);
            const source = parseCustomerEvidenceReference({
              ...event.evidence,
              version: "CustomerEvidenceReference.v1",
            });
            if (source?.id === evidenceId) {
              evidenceSourcesById.set(evidenceId, { ...source, title: label });
            }
          }
          return;
        }

        if (event.type === "result.available") {
          setAgentState((previous) => ({
            ...previous,
            turnPhases: {
              understand: previous.turnPhases?.understand ?? false,
              gather: previous.turnPhases?.gather ?? false,
              analyze: true,
              answer: previous.turnPhases?.answer ?? false,
            },
          }));
          resultSnapshot = {
            status: event.status,
            reconciliationStatus: event.reconciliationStatus,
            evidenceIds: [...event.evidenceIds],
          };
          return;
        }

        if (event.type === "turn.completed") {
          terminalSeen = true;
          clearQueuedTurnPause();
          if (cancellationRendered) {
            settleActiveTurn(true);
            return;
          }
          const responseText =
            streamedText.trim() ||
            (event.finishReason === "unsupported"
              ? "Nucleus couldn't answer that question with the available data."
              : safeFailureMessage);
          const evidenceLabels = resultSnapshot
            ? Array.from(
                new Set(
                  resultSnapshot.evidenceIds
                    .map((id) => evidenceLabelsById.get(id))
                    .filter((label): label is string => Boolean(label)),
                ),
              )
            : [];
          const sources = resultSnapshot
            ? resultSnapshot.evidenceIds
                .map((id) => evidenceSourcesById.get(id))
                .filter(
                  (source): source is CustomerEvidenceReference =>
                    source !== undefined,
                )
            : [];
          const grounding = resultSnapshot
            ? {
                status: resultSnapshot.status,
                reconciliationStatus: resultSnapshot.reconciliationStatus,
                evidenceLabels,
              }
            : event.finishReason === "unsupported"
              ? {
                  status: "unsupported" as const,
                  reconciliationStatus: "unsupported" as const,
                  evidenceLabels: [],
                }
              : undefined;
          appendMessage({
            ...(responseMessageId ? { id: responseMessageId } : {}),
            ...(activeTurnId ? { turnId: activeTurnId } : {}),
            type: "received",
            content: responseText,
            timestamp: new Date(),
            dataType: "text",
            nucleusStatus:
              event.finishReason === "unsupported" ? "unsupported" : "complete",
            ...(grounding ? { nucleusGrounding: grounding } : {}),
            ...(sources.length > 0 ? { nucleusSources: sources } : {}),
          });
          setAgentState((previous) => ({
            ...previous,
            status: "done",
            streamedText,
          }));
          settleActiveTurn(true);
          return;
        }

        if (event.type === "turn.failed") {
          terminalSeen = true;
          clearQueuedTurnPause();
          if (cancellationRendered) {
            settleActiveTurn(true);
            return;
          }
          appendMessage({
            id: `message-${createId()}`,
            turnId: event.turnId,
            type: "received",
            content: event.message,
            timestamp: new Date(),
            dataType: "text",
            nucleusStatus: "failed",
          });
          setAgentState((previous) => ({
            ...previous,
            status: "error",
            error: event.message,
          }));
          settleActiveTurn(true);
          return;
        }

        if (event.type === "turn.cancelled") {
          closeAuthoritativelyCancelledTurn();
        }
      };

      const streamWithToken = (token: string): Promise<void> => {
        if (
          generationRef.current !== generation ||
          controller.signal.aborted ||
          terminalSeen
        ) {
          return Promise.resolve();
        }
        try {
          return streamTurn({
            url: streamUrl,
            token,
            request: turn.request,
            signal: controller.signal,
            onEvent: handleEvent,
          });
        } catch (error) {
          return Promise.reject(error);
        }
      };
      const streamPromise = runAuthenticatedRequest(
        streamWithToken,
        (error) =>
          error instanceof NucleusSseError &&
          error.status === 401 &&
          !streamEventSeen &&
          !controller.signal.aborted &&
          generationRef.current === generation,
        controller.signal,
      );

      void streamPromise
        .then(() => {
          if (
            generationRef.current === generation &&
            !terminalSeen &&
            !controller.signal.aborted
          ) {
            throw new Error("Nucleus stream ended without a terminal event");
          }
        })
        .catch((error) => {
          if (generationRef.current !== generation || terminalSeen) return;
          if (controller.signal.aborted) {
            closeLocallyAbortedTurn();
            return;
          }
          if (error instanceof NucleusSessionExpiredError) {
            setAgentState(initialAgentState);
            clearQueuedTurns();
            clearQueuedTurnPause();
            settleActiveTurn(false);
            return;
          }

          const authenticationMessage = authenticationFailureMessage(error);
          const failureMessage = authenticationMessage ?? safeFailureMessage;
          appendMessage({
            type: "received",
            content: failureMessage,
            timestamp: new Date(),
            dataType: "text",
            nucleusStatus: "failed",
          });
          setAgentState({
            ...initialAgentState,
            status: "error",
            error: failureMessage,
          });
          if (authenticationMessage && !streamEventSeen) {
            // Authentication rejected the request before the platform could
            // start a turn, so there is no remote turn to overlap on retry.
            settleActiveTurn(false);
            if (queuedTurnsRef.current.length > 0) {
              pauseQueuedTurns("authentication_unavailable");
            }
            return;
          }
          pauseQueuedTurns("transport_unknown");
          setIsAwaitingResponse(false);
          setIsStopRequested(false);
        })
        .finally(() => {
          if (generationRef.current === generation) {
            abortRef.current = null;
            activeCancellationRef.current = null;
          }
        });
      return true;
    },
    [
      appendMessage,
      clearQueuedTurnPause,
      clearQueuedTurns,
      createId,
      pauseQueuedTurns,
      runAuthenticatedRequest,
      scheduleQueuedTurnDispatch,
      streamTurn,
      streamUrl,
    ],
  );

  dispatchQueuedTurnRef.current = dispatchPlatformTurn;

  const sendMessage = useCallback(
    (
      content: string,
      metricContext?: CommonMetricContext,
      viewContext?: CommonViewContextV1,
    ) => {
      const turn = createPlatformTurn(content, metricContext, viewContext);
      if (!turn) return;

      if (
        queuedTurnPauseReasonRef.current !== null ||
        inFlightRef.current ||
        activeTurnRef.current ||
        earlierMessagesAbortRef.current !== null ||
        queuedTurnsRef.current.length > 0
      ) {
        if (
          activeTurnRef.current?.dedupeKey === turn.dedupeKey ||
          queuedTurnsRef.current.some(
            (queued) => queued.dedupeKey === turn.dedupeKey,
          )
        ) {
          return;
        }
        replaceQueuedTurns([...queuedTurnsRef.current, turn]);
        if (
          queuedTurnPauseReasonRef.current === null &&
          !inFlightRef.current &&
          !activeTurnRef.current &&
          earlierMessagesAbortRef.current === null
        ) {
          scheduleQueuedTurnDispatch();
        }
        return;
      }

      dispatchPlatformTurn(turn);
    },
    [
      createPlatformTurn,
      dispatchPlatformTurn,
      replaceQueuedTurns,
      scheduleQueuedTurnDispatch,
    ],
  );

  const stopCurrentResponse = useCallback(
    (clearQueue = false) => {
      if (clearQueue) clearQueuedTurns();
      if (!abortRef.current || abortRef.current.signal.aborted) return;
      setIsStopRequested(true);
      abortRef.current.abort();
      activeCancellationRef.current?.();
    },
    [clearQueuedTurns],
  );

  useEffect(() => {
    const pendingAttempt = connectionAttemptRef.current;
    const boundCustomerId =
      activeCustomerIdRef.current ?? pendingAttempt?.organizationId;
    if (
      (isConnected || pendingAttempt) &&
      selectedCustomerId &&
      selectedCustomerId !== boundCustomerId
    ) {
      disconnectChat();
    }
  }, [disconnectChat, isConnected, isConnecting, selectedCustomerId]);

  useEffect(() => {
    const pendingAttempt = connectionAttemptRef.current;
    if (!isConnected && !pendingAttempt) return;
    const boundRun = activeRunRef.current ?? pendingAttempt?.run ?? null;
    if (runScopeIdentity(boundRun) === visibleRunScopeIdentity) {
      return;
    }
    const boundContext = activeContextRef.current ?? pendingAttempt?.context;
    if (!visibleRun || !boundContext) {
      disconnectChat();
      return;
    }

    // A run change creates a new conversation. Never let a sidebar labelled
    // with the new run continue sending the old run's scope.
    connectChat(visibleRun, boundContext);
  }, [
    chatContext,
    connectChat,
    disconnectChat,
    isConnected,
    isConnecting,
    visibleRun,
    visibleRunScopeIdentity,
  ]);

  useEffect(
    () => () => {
      generationRef.current += 1;
      connectionAttemptRef.current = null;
      readinessAbortRef.current?.abort();
      historyAbortRef.current?.abort();
      earlierMessagesAbortRef.current?.abort();
      abortRef.current?.abort();
    },
    [],
  );

  const activeScopeMatchesVisible =
    isConnected &&
    visibleConnectionScopeKey !== null &&
    connectedScopeKeyRef.current === visibleConnectionScopeKey &&
    activeCustomerIdRef.current === visibleOrganizationId &&
    runScopeIdentity(activeRunRef.current) === visibleRunScopeIdentity;
  const visiblePlatformConnectionFailure =
    platformConnectionFailure?.scopeKey === visibleConnectionScopeKey
      ? platformConnectionFailure
      : null;

  const value: ChatContextType = {
    transportKind: "platform",
    transportCapabilities: {
      history: true,
      queue: true,
      resume: true,
    },
    isConnected: activeScopeMatchesVisible,
    isConnecting,
    messages: activeScopeMatchesVisible ? messages : [],
    setMessages,
    sendMessage,
    registerCurrentViewContext,
    registerCurrentViewData,
    registerActiveMetricContext,
    connectChat,
    startNewConversation,
    disconnectChat,
    isAwaitingResponse: activeScopeMatchesVisible ? isAwaitingResponse : false,
    chatSessionId: activeScopeMatchesVisible ? chatSessionId : null,
    chatContext: activeScopeMatchesVisible ? chatContext : null,
    platformConnectionError: visiblePlatformConnectionFailure?.message ?? null,
    platformConnectionErrorKind: visiblePlatformConnectionFailure?.kind ?? null,
    agentState: activeScopeMatchesVisible ? agentState : initialAgentState,
    chatHistory: [],
    chatHistoryLoading: false,
    fetchChatHistory: () => {},
    loadChatFromHistory: (_historyItem: ChatHistoryItem) => {},
    platformConversationHistory: activeScopeMatchesVisible
      ? platformConversationHistory
      : [],
    platformConversationHistoryLoading: activeScopeMatchesVisible
      ? platformConversationHistoryLoading
      : false,
    platformConversationHistoryError: activeScopeMatchesVisible
      ? platformConversationHistoryError
      : null,
    refreshPlatformConversationHistory,
    resumePlatformConversation,
    platformConversationHasEarlierMessages:
      activeScopeMatchesVisible &&
      earlierMessagesCursorRef.current !== null &&
      !isAwaitingResponse &&
      queuedTurns.length === 0 &&
      queuedTurnPauseReason === null,
    platformEarlierMessagesLoading: activeScopeMatchesVisible
      ? platformEarlierMessagesLoading
      : false,
    platformEarlierMessagesError: activeScopeMatchesVisible
      ? platformEarlierMessagesError
      : null,
    loadEarlierPlatformConversationMessages,
    stopCurrentResponse,
    queuedMessages: activeScopeMatchesVisible ? queuedTurns.length : 0,
    queuedPrompts: activeScopeMatchesVisible
      ? queuedTurns.map((turn) => turn.request.message.text)
      : [],
    queuedPromptItems: activeScopeMatchesVisible
      ? queuedTurns.map(
          (turn): QueuedChatPrompt => ({
            id: turn.id,
            text: turn.request.message.text,
          }),
        )
      : [],
    cancelQueuedPrompt,
    queuedDispatchPaused:
      activeScopeMatchesVisible && queuedTurnPauseReason !== null,
    queuedDispatchPauseMessage:
      activeScopeMatchesVisible && queuedTurnPauseReason !== null
        ? queuedTurnPauseMessage(queuedTurnPauseReason)
        : null,
    queuedDispatchCanRetry:
      activeScopeMatchesVisible &&
      queuedTurnPauseReason === "authentication_unavailable" &&
      queuedTurns.length > 0 &&
      !platformEarlierMessagesLoading,
    retryQueuedDispatch,
    isStopRequested: activeScopeMatchesVisible ? isStopRequested : false,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
